import styled from 'styled-components';
import { RightArrowIcon, SearchIcon } from '../../components/icon/icon';
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ManagerSettingData, UserSettingData } from '../../utils/menu';
import {
  cancelDelegation,
  fetchCompanyMypage,
  fetchDriverMypage,
} from '../../api/mypage';
import { AlertModal } from '../../components/modal/alert.modal';
import { useRecoilValue } from 'recoil';
import { authState } from '../../atoms/authState';

const animation = {
  open: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 40,
    },
  },
  closed: {
    y: '100%',
    opacity: 0,
    transition: {
      type: 'spring',
      stiffness: 900,
      damping: 40,
    },
  },
  exit: {
    opacity: 0,
    y: '100%',
    transition: {
      type: 'spring',
      stiffness: 900,
      damping: 40,
    },
  },
};

interface DriverState {
  vehicle: string;
  phone: string;
  registration: string;
  to_user_id: string | null;
}

interface CompanyState {
  passid: string;
  name: string;
  charge: string;
  position: string;
  phone: string;
  address: string;
  registration: string;
}

const Mypage = () => {
  const auth = useRecoilValue(authState);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  // 상태 정의
  const [profileData, setProfileData] = useState<DriverState | null>(null);
  console.log(profileData);

  const [companyProfileData, setCompanyProfileData] =
    useState<CompanyState | null>(null);

  const [fullImg, setFullImg] = useState(false);

  // 마이페이지 데이터 가져오기
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (auth.userType === 'driver') {
          // 드라이버 데이터 가져오기
          const driverData = await fetchDriverMypage();
          setProfileData(driverData);
        } else if (auth.userType === 'company') {
          // 회사 데이터 가져오기 (추가적인 API 호출이 필요합니다)
          const companyData = await fetchCompanyMypage(); // fetchCompanyMypage()는 회사 데이터를 가져오는 API 호출 함수라고 가정합니다.
          setCompanyProfileData(companyData);
        }
      } catch (error) {
        console.error('마이페이지 데이터를 불러오는 데 실패했습니다.');
      }
    };

    fetchData();
  }, [auth.userType]);

  const handleClick = () => {
    setFullImg(true);
  };
  const handleClickOut = () => {
    setFullImg(false);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleCancelDelegation = async () => {
    try {
      const response = await cancelDelegation();
      setModalMessage(response.message); // API 응답 메시지 설정
      setIsModalOpen(true); // 모달 열기
      // 위임 취소가 성공적으로 완료되었을 때 profileData를 업데이트하여 to_user_id를 null로 설정
      setProfileData((prevData) =>
        prevData ? { ...prevData, to_user_id: null } : prevData,
      );
    } catch (error) {
      console.error('위임 취소 중 오류가 발생했습니다:', error);
      setModalMessage('위임 취소 중 오류가 발생했습니다.');
      setIsModalOpen(true); // 모달 열기
    }
  };
  return (
    <Container>
      {auth.userType === 'driver' ? (
        <ProfileImgSection>
          <div className="inner">
            <div className="img-box">
              <img src={profileData?.registration} alt="자동차 등록증" />
            </div>
            <button className="btn" onClick={handleClick}>
              <SearchIcon />
            </button>
          </div>
          {profileData?.to_user_id ? (
            <div className="delegation-info">
              <div className="information-txt">
                현재 수주 권한이 <span>{profileData?.to_user_id}</span>{' '}
                차주님에게 <br /> 위임되어 있습니다.
              </div>
              <div className="btn-box">
                <button onClick={handleCancelDelegation}>위임 취소</button>
              </div>
            </div>
          ) : (
            <div className="delegation-info center">
              <div className="information-txt">
                수주 권한은 해당 계정에 있습니다.
              </div>
            </div>
          )}
          <AnimatePresence>
            {fullImg ? (
              <FullImgBox
                variants={animation}
                initial="closed"
                animate="open"
                exit="exit"
                onClick={handleClickOut}
              >
                <div className="img-box">
                  <img src={profileData?.registration} alt="자동차 등록증" />
                </div>
              </FullImgBox>
            ) : null}
          </AnimatePresence>
        </ProfileImgSection>
      ) : (
        <ProfileImgSection>
          <div className="inner">
            <div className="img-box">
              <img src={companyProfileData?.registration} alt="사업자 등록증" />
            </div>
            <button className="btn" onClick={handleClick}>
              <SearchIcon />
            </button>
          </div>
          <AnimatePresence>
            {fullImg ? (
              <FullImgBox
                variants={animation}
                initial="closed"
                animate="open"
                exit="exit"
                onClick={handleClickOut}
              >
                <div className="img-box">
                  <img
                    src={companyProfileData?.registration}
                    alt="사업자 등록증"
                  />
                </div>
              </FullImgBox>
            ) : null}
          </AnimatePresence>
        </ProfileImgSection>
      )}
      <UserInfoSection>
        <ul>
          <li>
            <span className="label">아이디</span>
            <span className="value">
              {auth.userType === 'driver'
                ? profileData?.vehicle
                : companyProfileData?.passid}
            </span>
          </li>
          {auth.userType === 'company' && (
            <li>
              <span className="label">회사명</span>
              <span className="value">{companyProfileData?.name}</span>
            </li>
          )}
          {auth.userType === 'company' && (
            <li>
              <span className="label">담당자</span>
              <span className="value">
                {companyProfileData?.position} {companyProfileData?.charge}
              </span>
            </li>
          )}
          <li>
            <span className="label">휴대폰 번호</span>
            <span className="value">
              {auth.userType === 'driver'
                ? profileData?.phone
                : companyProfileData?.phone}
            </span>
          </li>
          {auth.userType === 'company' && (
            <li>
              <span className="label">주소</span>
              <span className="value">{companyProfileData?.address}</span>
            </li>
          )}
        </ul>
      </UserInfoSection>
      <UserSettingSection>
        {auth.userType === 'driver' && (
          <ul>
            {UserSettingData.map((setting, index) => (
              <li key={index}>
                <Link
                  to={{
                    pathname: setting.path,
                  }}
                >
                  <span className="title">{setting.title}</span>
                  <span className="icon-box">
                    <RightArrowIcon />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {auth.userType === 'company' && (
          <ul>
            {ManagerSettingData.map((setting, index) => (
              <li key={index}>
                <Link
                  to={{
                    pathname: setting.path,
                  }}
                >
                  <span className="title">{setting.title}</span>
                  <span className="icon-box">
                    <RightArrowIcon />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </UserSettingSection>
      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default Mypage;

// style
const Container = styled.div`
  height: 100%;
  overflow: hidden scroll;
  padding: 2.4rem 1.5rem 12rem;
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

const ProfileImgSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 2.4rem;
  .inner {
    position: relative;
    .img-box {
      display: flex;
      width: 13.4rem;
      height: 13.4rem;
      overflow: hidden;
      border-radius: 0.8rem;
      border: 1px inset rgba(0, 0, 0, 0.12);
      img {
        max-height: 100%;
        max-width: 100%;
        object-fit: cover;
      }
    }
    .btn {
      outline: none;
      border: none;
      background-color: transparent;
      position: absolute;
      right: -1rem;
      bottom: -1rem;
    }
  }
  .delegation-info {
    margin-top: 2.4rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    width: 100%;
    &.center {
      justify-content: space-between;
    }
    .information-txt {
      font-size: 1.2rem;
      font-weight: 500;
      color: ${(props) => props.theme.joinLabelColor};
      span {
        color: ${(props) => props.theme.btnBlueColor};
        font-weight: 600;
      }
    }
    .btn-box {
      display: flex;
      button {
        padding: 0.8rem 1rem;
        white-space: nowrap;
        border: none;
        outline: none;
        background-color: ${(props) => props.theme.errorColor};
        color: ${(props) => props.theme.textWhiteColor};
        border-radius: 0.4rem;
      }
    }
  }
`;

const FullImgBox = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  padding: 1.4rem;
  z-index: 999;
  .img-box {
    display: flex;
    align-items: center;
    justify-content: center;
    transform: translateY(60%);
    img {
      max-width: 100%;
    }
  }
`;

const UserInfoSection = styled.section`
  background-color: ${(props) => props.theme.textWhiteColor};
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 2px 6px ${(props) => props.theme.f6f6f6};
  ul {
    padding: 0.8rem 0;
    li {
      padding: 1.6rem 2.4rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      span {
        font-size: 1.6rem;
        font-weight: 500;
      }
      .label {
      }
      .value {
        color: ${(props) => props.theme.textAAAColor};
      }
      &:not(:first-child) {
        border-top: 1px solid ${(props) => props.theme.f5f5f5};
      }
    }
  }
`;

const UserSettingSection = styled.section`
  margin-top: 2.4rem;
  background-color: ${(props) => props.theme.textWhiteColor};
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 2px 6px ${(props) => props.theme.f6f6f6};
  ul {
    padding: 0.8rem 0;
    li {
      a {
        padding: 1.4rem 2.4rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        span {
          font-size: 1.6rem;
          font-weight: 500;
        }
        .icon-box {
          svg {
            path {
              fill: ${(props) => props.theme.loginHrColor};
              fill-opacity: 1;
            }
          }
        }
      }
      &:not(:first-child) {
        border-top: 1px solid ${(props) => props.theme.f5f5f5};
      }
    }
  }
`;
